import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface AgentContext {
  route?: string;
  email?: string;
  orderId?: string;
}

/** A prompt pushed straight into the chat drawer (opens it and sends). */
export interface AgentPromptRequest {
  message: string;
  /** Bind the new conversation to this agent role. */
  roleId?: string | null;
  /** Run the message under this instruction/workflow quick-action. */
  instructionId?: string | null;
}

/** A request to let the operator pick agent + instruction before sending. */
export interface AgentPickerRequest {
  /** Prepared user input — shown in the dialog, editable by the operator. */
  message: string;
  /** Optional context line shown in the dialog header (e.g. patient email). */
  title?: string;
}

@Injectable({ providedIn: 'root' })
export class AiChatPromptService {
  private promptSubject = new Subject<AgentPromptRequest>();
  prompt$ = this.promptSubject.asObservable();

  private pickerSubject = new Subject<AgentPickerRequest>();
  picker$ = this.pickerSubject.asObservable();

  private toggleSubject = new Subject<void>();
  toggle$ = this.toggleSubject.asObservable();

  private contextSubject = new BehaviorSubject<AgentContext>({});
  context$ = this.contextSubject.asObservable();

  sendPrompt(message: string, opts?: { roleId?: string | null; instructionId?: string | null }): void {
    this.promptSubject.next({ message, roleId: opts?.roleId || null, instructionId: opts?.instructionId || null });
  }

  /**
   * Open the agent picker dialog: the operator chooses a role and optionally
   * an instruction/workflow, can edit the prepared message, and only then is
   * the chat drawer opened and the message sent (via sendPrompt).
   */
  openAgentPicker(request: AgentPickerRequest): void {
    this.pickerSubject.next(request);
  }

  toggle(): void {
    this.toggleSubject.next();
  }

  setContext(ctx: AgentContext): void {
    this.contextSubject.next(ctx);
  }

  getContext(): AgentContext {
    return this.contextSubject.value;
  }
}
